import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { toast } from '@/lib/toast';
import { Segmented } from '@/components/Segmented';
import { useCurrentUser } from '@/features/auth/useAuth';
import { taskKeys } from './useTasks';

export type RecurrenceMode = 'fixed_schedule' | 'after_completion';

export interface Recurrence {
  id: number;
  task_id: number;
  mode: RecurrenceMode;
  rrule: string | null;
  interval_days: number | null;
  timezone: string;
  all_day: boolean;
  exdates: string[] | null;
  dtstart: string | null;
  next_due_at: string | null;
}

type ModeChoice = RecurrenceMode | 'none';
type IntervalUnit = 'day' | 'week' | 'month' | 'year';

const AFTER_DAY_PRESETS = [1, 2, 3, 7, 14, 30] as const;

const SCHEDULE_PRESETS: { label: string; rrule: string }[] = [
  { label: 'Daily', rrule: 'FREQ=DAILY' },
  { label: 'Weekdays', rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR' },
  { label: 'Weekly', rrule: 'FREQ=WEEKLY' },
  { label: 'Biweekly', rrule: 'FREQ=WEEKLY;INTERVAL=2' },
  { label: 'Monthly', rrule: 'FREQ=MONTHLY' },
  { label: 'Quarterly', rrule: 'FREQ=MONTHLY;INTERVAL=3' },
  { label: 'Annually', rrule: 'FREQ=YEARLY' },
];

const UNIT_LABEL: Record<IntervalUnit, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
};

function daysForUnit(n: number, unit: IntervalUnit): number {
  switch (unit) {
    case 'day':
      return n;
    case 'week':
      return n * 7;
    case 'month':
      return n * 30;
    case 'year':
      return n * 365;
  }
}

function rruleForUnit(n: number, unit: IntervalUnit): string {
  const freq =
    unit === 'day'
      ? 'DAILY'
      : unit === 'week'
        ? 'WEEKLY'
        : unit === 'month'
          ? 'MONTHLY'
          : 'YEARLY';
  return n <= 1 ? `FREQ=${freq}` : `FREQ=${freq};INTERVAL=${n}`;
}

function parseRruleCustom(rrule: string | null): { n: number; unit: IntervalUnit } | null {
  if (!rrule) return null;
  if (SCHEDULE_PRESETS.some((p) => p.rrule === rrule)) return null;
  const freq = /FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)/i.exec(rrule)?.[1]?.toUpperCase();
  if (!freq) return null;
  if (/BYDAY=/i.test(rrule)) return null;
  const interval = Number(/INTERVAL=(\d+)/i.exec(rrule)?.[1] ?? '1');
  if (!Number.isFinite(interval) || interval < 1) return null;
  const unit: IntervalUnit =
    freq === 'DAILY'
      ? 'day'
      : freq === 'WEEKLY'
        ? 'week'
        : freq === 'MONTHLY'
          ? 'month'
          : 'year';
  return { n: interval, unit };
}

function parseAfterCustom(days: number | null): { n: number; unit: IntervalUnit } {
  if (days == null || days < 1) return { n: 90, unit: 'day' };
  if (days % 365 === 0) return { n: days / 365, unit: 'year' };
  if (days % 30 === 0 && days >= 30) return { n: days / 30, unit: 'month' };
  if (days % 7 === 0) return { n: days / 7, unit: 'week' };
  return { n: days, unit: 'day' };
}

export function RecurrenceSection({
  taskId,
  compact = false,
}: {
  taskId: number;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  const user = useCurrentUser();
  const key = ['task-recurrence', taskId] as const;

  const [afterCustomOpen, setAfterCustomOpen] = useState(false);
  const [scheduleCustomOpen, setScheduleCustomOpen] = useState(false);
  const [customN, setCustomN] = useState(3);
  const [customUnit, setCustomUnit] = useState<IntervalUnit>('month');

  const existing = useQuery({
    queryKey: key,
    queryFn: () => api<Recurrence | null>(`/api/tasks/${taskId}/recurrence`),
  });

  const invalidateTask = () => {
    void qc.invalidateQueries({ queryKey: taskKeys.all });
    void qc.invalidateQueries({ queryKey: key });
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<Recurrence>(`/api/tasks/${taskId}/recurrence`, { method: 'PUT', json: body }),
    onSuccess: (row) => {
      qc.setQueryData(key, row);
      invalidateTask();
      toast.show('Recurrence saved', { tone: 'success' });
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const clear = useMutation({
    mutationFn: () => api<void>(`/api/tasks/${taskId}/recurrence`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.setQueryData(key, null);
      setAfterCustomOpen(false);
      setScheduleCustomOpen(false);
      invalidateTask();
      toast.show('Recurrence removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const row = existing.data ?? null;
  const timezone = user.data?.timezone ?? 'UTC';
  const mode: ModeChoice = row?.mode ?? 'none';

  const afterIsPreset =
    row?.mode === 'after_completion' &&
    row.interval_days != null &&
    (AFTER_DAY_PRESETS as readonly number[]).includes(row.interval_days);
  const afterIsCustom =
    row?.mode === 'after_completion' && row.interval_days != null && !afterIsPreset;
  const scheduleCustom = parseRruleCustom(row?.rrule ?? null);
  const scheduleIsCustom = row?.mode === 'fixed_schedule' && scheduleCustom != null;

  useEffect(() => {
    if (afterIsCustom && row?.interval_days != null && !afterCustomOpen) {
      const parsed = parseAfterCustom(row.interval_days);
      setCustomN(parsed.n);
      setCustomUnit(parsed.unit);
    }
  }, [afterIsCustom, afterCustomOpen, row?.interval_days]);

  useEffect(() => {
    if (scheduleIsCustom && row?.rrule && !scheduleCustomOpen) {
      const parsed = parseRruleCustom(row.rrule);
      if (parsed) {
        setCustomN(parsed.n);
        setCustomUnit(parsed.unit);
      }
    }
  }, [scheduleIsCustom, scheduleCustomOpen, row?.rrule]);

  const chipClass = (active: boolean) =>
    cn(
      'pressable rounded-full text-label transition-[background-color,color,transform]',
      'duration-press ease-[cubic-bezier(0.32,0.72,0,1)]',
      compact ? 'min-h-control px-2.5' : 'min-h-touch px-3 text-small',
      active
        ? 'bg-accent text-text-on-accent'
        : 'bg-surface-2 text-text-secondary hover:bg-surface-3',
    );

  const openAfterCustom = () => {
    const parsed = parseAfterCustom(row?.interval_days ?? 90);
    setCustomN(parsed.n);
    setCustomUnit(parsed.unit);
    setAfterCustomOpen(true);
    setScheduleCustomOpen(false);
  };

  const openScheduleCustom = () => {
    const parsed = scheduleCustom ?? { n: 3, unit: 'month' as const };
    setCustomN(parsed.n);
    setCustomUnit(parsed.unit);
    setScheduleCustomOpen(true);
    setAfterCustomOpen(false);
  };

  return (
    <div className={cn('flex flex-col', compact ? 'gap-2' : 'gap-3')}>
      <Segmented
        compact={compact}
        label="Repeat"
        value={mode}
        options={[
          { value: 'none', label: 'Off' },
          { value: 'after_completion', label: 'After done' },
          { value: 'fixed_schedule', label: 'On a schedule' },
        ]}
        onChange={(v) => {
          if (v === 'none') {
            if (row) clear.mutate();
            return;
          }
          setAfterCustomOpen(false);
          setScheduleCustomOpen(false);
          if (v === 'after_completion') {
            save.mutate({
              mode: 'after_completion',
              interval_days: row?.interval_days ?? 3,
              timezone,
              all_day: true,
            });
            return;
          }
          save.mutate({
            mode: 'fixed_schedule',
            rrule: row?.rrule ?? 'FREQ=WEEKLY',
            timezone,
            all_day: true,
          });
        }}
      />

      {row?.mode === 'after_completion' && (
        <div>
          <p className={cn('text-text-secondary', compact ? 'mb-1.5 text-label' : 'mb-2 text-small')}>
            Every N days after you complete it.
          </p>
          <div className="flex flex-wrap gap-1">
            {AFTER_DAY_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                aria-pressed={!afterCustomOpen && row.interval_days === n}
                onClick={() => {
                  setAfterCustomOpen(false);
                  save.mutate({
                    mode: 'after_completion',
                    interval_days: n,
                    timezone,
                    all_day: true,
                  });
                }}
                className={chipClass(!afterCustomOpen && row.interval_days === n)}
              >
                {n}d
              </button>
            ))}
            <button
              type="button"
              aria-pressed={afterCustomOpen || afterIsCustom}
              onClick={openAfterCustom}
              className={chipClass(afterCustomOpen || afterIsCustom)}
            >
              Custom
            </button>
          </div>
          {(afterCustomOpen || afterIsCustom) && (
            <CustomIntervalForm
              compact={compact}
              n={customN}
              unit={customUnit}
              onN={setCustomN}
              onUnit={setCustomUnit}
              saving={save.isPending}
              onApply={() => {
                const days = daysForUnit(customN, customUnit);
                if (days < 1 || days > 3650) {
                  toast.error('Choose an interval between 1 day and 10 years');
                  return;
                }
                save.mutate(
                  {
                    mode: 'after_completion',
                    interval_days: days,
                    timezone,
                    all_day: true,
                  },
                  { onSuccess: () => setAfterCustomOpen(false) },
                );
              }}
            />
          )}
        </div>
      )}

      {row?.mode === 'fixed_schedule' && (
        <div>
          <p className={cn('text-text-secondary', compact ? 'mb-1.5 text-label' : 'mb-2 text-small')}>
            Completing advances to the next occurrence.
          </p>
          <div className="flex flex-wrap gap-1">
            {SCHEDULE_PRESETS.map((p) => (
              <button
                key={p.rrule}
                type="button"
                aria-pressed={!scheduleCustomOpen && row.rrule === p.rrule}
                onClick={() => {
                  setScheduleCustomOpen(false);
                  save.mutate({
                    mode: 'fixed_schedule',
                    rrule: p.rrule,
                    timezone,
                    all_day: true,
                  });
                }}
                className={chipClass(!scheduleCustomOpen && row.rrule === p.rrule)}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={scheduleCustomOpen || scheduleIsCustom}
              onClick={openScheduleCustom}
              className={chipClass(scheduleCustomOpen || scheduleIsCustom)}
            >
              Custom
            </button>
          </div>
          {(scheduleCustomOpen || scheduleIsCustom) && (
            <CustomIntervalForm
              compact={compact}
              n={customN}
              unit={customUnit}
              onN={setCustomN}
              onUnit={setCustomUnit}
              saving={save.isPending}
              onApply={() => {
                if (customN < 1 || customN > 999) {
                  toast.error('Enter a positive interval');
                  return;
                }
                save.mutate(
                  {
                    mode: 'fixed_schedule',
                    rrule: rruleForUnit(customN, customUnit),
                    timezone,
                    all_day: true,
                  },
                  { onSuccess: () => setScheduleCustomOpen(false) },
                );
              }}
            />
          )}
        </div>
      )}

      {row?.next_due_at && (
        <p className="text-label text-text-tertiary">
          Next due {new Date(row.next_due_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function CustomIntervalForm({
  compact,
  n,
  unit,
  onN,
  onUnit,
  onApply,
  saving,
}: {
  compact: boolean;
  n: number;
  unit: IntervalUnit;
  onN: (n: number) => void;
  onUnit: (u: IntervalUnit) => void;
  onApply: () => void;
  saving: boolean;
}) {
  return (
    <div
      className={cn(
        'mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 p-2',
        compact ? 'text-label' : 'text-small',
      )}
    >
      <span className="text-text-secondary">Every</span>
      <input
        type="number"
        min={1}
        max={999}
        value={n}
        onChange={(e) => onN(Math.max(1, Number(e.target.value) || 1))}
        className={cn(
          'w-16 rounded border border-border bg-surface px-2 py-1 text-text-primary',
          'focus:border-accent outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      />
      <select
        value={unit}
        onChange={(e) => onUnit(e.target.value as IntervalUnit)}
        className={cn(
          'rounded border border-border bg-surface px-2 py-1 text-text-primary',
          'focus:border-accent outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        {(Object.keys(UNIT_LABEL) as IntervalUnit[]).map((u) => (
          <option key={u} value={u}>
            {UNIT_LABEL[u]}
            {n === 1 ? '' : 's'}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={saving}
        onClick={onApply}
        className={cn(
          'pressable rounded-full bg-accent px-3 text-text-on-accent',
          compact ? 'min-h-control text-label' : 'min-h-touch text-small',
        )}
      >
        Apply
      </button>
    </div>
  );
}
